import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme, Tooltip } from '@mui/material';
import { AnyAction } from '@reduxjs/toolkit';
import { IconName, IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    ContextActionType,
    CustomAvatar,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    ModalContext,
    SkeletonLoader,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import { plusIcon, FaOptionIcon, companyIcon, userIcon } from '@europrocurement/l2d-icons';
import {
    SousClient,
    SousClientsSelector,
    useTiersService,
    TiersAppDispatch,
    SousClientApi,
    useTiersServiceSelector,
    selectedPrescripteursSelector,
    TiersObjectGroup,
    DOSSIERS_OPEN_SEARCH_SLICE_NAME,
    useSociete,
} from '@europrocurement/l2d-domain';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { PrescriberClientForm } from '@europrocurement/l2d-modules/modules/PrescriberClient/components/AddPrescriberClient';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import ActionMenuSocietes from '../../components/ActionMenuSocietes';
import { useSocieteFilter, useSocietesObjectGroup } from '../../hooks';
import { PossibleTypes, useSearchBar } from '../../providers/SearchBarProvider';

export type ComponentListeSousClientProps = {
    showPrescripteur: boolean;
};

/**
 * Formate une chaîne (téléphone ou SIREN) avec des espaces.
 */
function normalizeString(data: string | undefined, type: 'phone' | 'siren' = 'phone'): string {
    if (!data) return '';

    const cleaned = data.replace(/\s+/g, '');

    if (type === 'siren') {
        return cleaned.match(/.{1,3}/g)?.join(' ') || '';
    }

    const match = cleaned.match(/^(\+\d+)(.*)$/);
    if (match) {
        const [, internationalCode, restOfNumber] = match;
        const formattedRest = restOfNumber.match(/.{1,2}/g)?.join(' ') || '';
        return `${internationalCode} ${formattedRest}`.trim();
    }

    return cleaned.match(/.{1,2}/g)?.join(' ') || '';
}

/**
 * Page de listing des sociétés (sous-clients).
 */
const ListeSousClient: React.FunctionComponent<ComponentListeSousClientProps> = function ({
    showPrescripteur = false,
}) {
    const theme = useTheme();
    const { palette } = theme;
    const navigate = useNavigate();
    const dispatch = useDispatch<TiersAppDispatch>();

    const xIdSociete = useSociete();
    const { modalActions } = React.useContext(ModalContext);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();

    const datasource: DataSource<SousClient> = useSelector(SousClientsSelector).main;
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);

    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);
    const { getSousClients, selectPrescripteur } = useTiersService();

    const { searchTiersObjectGroup } = useSocietesObjectGroup();
    const { searchSociete } = useSocieteFilter();

    const breadcrumbPath = React.useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/societes', path: 'Sociétés' },
            'Liste de mes sociétés',
        ],
        [],
    );

    // Re-fetch des données quand les filtres changent
    React.useEffect(() => {
        dispatch(getSousClients({}) as unknown as AnyAction);
    }, [datasource.filters, dispatch, getSousClients]);

    const handleRedirectToDossiers = React.useCallback(
        (e: React.MouseEvent<HTMLElement>, item: SousClientApi) => {
            e.preventDefault();
            dispatch({ type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/deletemainPreFilter` });
            dispatch({ type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/resetmainFilter` });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'clientId', value: [item.id] },
            });
            navigate('/dossiers');
        },
        [dispatch, navigate],
    );

    const handleModalClose = React.useCallback(() => {
        modalActions.close();
    }, [modalActions]);

    const handleCreateSuccess = React.useCallback(() => {
        setTimeout(() => {
            dispatch({
                type: 'sousClient/setmainPagination',
                payload: { page: 0, itemsPerPage: 10 },
            });
            dispatch(getSousClients({}) as unknown as AnyAction);
            modalActions.close();
        }, 1000);
    }, [dispatch, getSousClients, modalActions]);

    const openPrescriberClientForm = React.useCallback(
        (sousClient?: SousClientApi) => {
            modalActions.call(
                <PrescriberClientForm
                    onCancel={handleModalClose}
                    onSuccess={handleCreateSuccess}
                    sousClient={sousClient}
                />,
            );
        },
        [handleModalClose, handleCreateSuccess, modalActions],
    );

    const handleCreateClient = React.useCallback(() => {
        openPrescriberClientForm();
    }, [openPrescriberClientForm]);

    const handleRowClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, sousClient: SousClientApi) => {
            if (!prescripteur || prescripteur?.id !== sousClient.idPrescripteur) {
                dispatch(selectPrescripteur({ id: sousClient.idPrescripteur })).then(() => {
                    openPrescriberClientForm(sousClient);
                });
            } else {
                openPrescriberClientForm(sousClient);
            }
        },
        [dispatch, openPrescriberClientForm, prescripteur, selectPrescripteur],
    );

    const handlePrescripteurClick = React.useCallback(
        (e: React.MouseEvent<HTMLElement>, item: SousClientApi) => {
            e.preventDefault();
            navigate(`/prescripteurs/liste/${item.idPrescripteur}?prescriber-detail=resume`);
        },
        [navigate],
    );

    // Activation du flag de recherche sociétés
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.Society);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    // Re-fetch des données quand les filtres changent
    React.useEffect(() => {
        dispatch(getSousClients({}) as unknown as AnyAction);
    }, [datasource.filters, dispatch, getSousClients]);

    const columns: ColumnDatatable<SousClientApi>[] = React.useMemo(() => {
        const baseColumns: ColumnDatatable<SousClientApi>[] = [
            {
                label: 'Statut',
                displayLabel: false,
                render: ({ personnePhysique, demo }) => (
                    <div>
                        {personnePhysique ? (
                            <FaOptionIcon
                                color={demo ? 'error' : 'primary'}
                                {...userIcon.props}
                            />
                        ) : (
                            <FaOptionIcon
                                color={demo ? 'error' : 'primary'}
                                {...companyIcon.props}
                            />
                        )}
                    </div>
                ),
                isDisplayed: true,
                sortable: true,
                field: 'personnePhysique',
            },
            {
                isDisplayed: true,
                sortable: true,
                label: 'Denomination',
                field: 'raisonSociale',
                sx: {
                    display: {
                        xs: 'none',
                        sm: 'none',
                        md: 'table-cell',
                        lg: 'table-cell',
                        xl: 'table-cell',
                    },
                    fontWeight: 500,
                    paddingLeft: '40px',
                },
                render: (sousClient: SousClientApi) => {
                    const adressePrincipal = sousClient.adresses.find((adresse) => adresse.defaut);
                    let addressConcat: string | null = null;

                    if (adressePrincipal) {
                        const fields = [
                            adressePrincipal.adresse1,
                            adressePrincipal.adresse2,
                            adressePrincipal.adresse3,
                            adressePrincipal.adresse4,
                            adressePrincipal.codePostal,
                            adressePrincipal.ville,
                        ];
                        addressConcat = fields
                            .filter((field) => field != null && field.trim() !== '')
                            .join(' ');
                    }

                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris70"
                            >
                                {sousClient.raisonSociale}
                            </Typography>
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris50"
                            >
                                {addressConcat}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                isDisplayed: true,
                label: 'Identifiant fiscal',
                sortable: true,
                field: 'siren',
                render: (sousClient: SousClientApi) => {
                    // Check if the sousClient has a siren, or a tvaIntracommunautaire
                    // Return siren in priority, then tva or display nothing
                    if (!sousClient.siren && !sousClient.tvaIntracommunautaire) return null;

                    const sirenOrTva: string = sousClient.siren
                        ? `Siren : ${normalizeString(sousClient.siren, 'siren')}`
                        : `Id fiscal : ${normalizeString(sousClient.tvaIntracommunautaire)}`;

                    return (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {sirenOrTva}
                        </Typography>
                    );
                },
            },
            {
                isDisplayed: true,
                label: 'Téléphone',
                field: 'contacts',
                sortable: false,
                render: (sousClient: SousClientApi) => {
                    const contactPrincipal = sousClient.contacts.find(
                        (contact) => contact.principal,
                    );
                    if (!contactPrincipal) return null;

                    const telephonesContactPrincipal = contactPrincipal.telephonesContact.find(
                        (tel) => tel.defaut,
                    );
                    const mobileContactPrincipal = contactPrincipal.mobilesContact.find(
                        (mobile) => mobile.defaut,
                    );
                    const numeroToShow =
                        telephonesContactPrincipal?.telephone ?? mobileContactPrincipal?.mobile;

                    if (!numeroToShow) return null;

                    return (
                        <Box
                            sx={{
                                color: palette.grey[700],
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '5px',
                            }}
                        >
                            <FaOptionIcon icon={['fasl', 'phone']} />
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris70"
                            >
                                {normalizeString(numeroToShow)}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                isDisplayed: true,
                sortable: false,
                label: 'Email',
                field: 'contacts',
                render: (sousClient: SousClientApi) => {
                    const contactPrincipal = sousClient.contacts.find(
                        (contact) => contact.principal,
                    );
                    if (!contactPrincipal) return null;

                    const emailContactPrincipal = contactPrincipal.emailsContact.find(
                        (email) => email.defaut,
                    );
                    if (!emailContactPrincipal?.email) return null;

                    return (
                        <Box
                            sx={{
                                color: palette.grey[700],
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '5px',
                            }}
                        >
                            <FaOptionIcon icon={['fasl', 'envelope']} />
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris70"
                            >
                                <a
                                    href={`mailto:${emailContactPrincipal.email}`}
                                    style={{ color: 'inherit', textDecoration: 'inherit' }}
                                >
                                    {emailContactPrincipal.email}
                                </a>
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                isDisplayed: true,
                label: 'Date Créa...',
                sortable: true,
                field: 'dateCreation',
                headerTooltip: 'Date Création',
                render: (sousClient: SousClientApi) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {sousClient.dateCreation
                            ? new Date(sousClient.dateCreation).toLocaleDateString()
                            : '-'}
                    </Typography>
                ),
            },
            {
                isDisplayed: true,
                label: 'Modifié le',
                sortable: true,
                field: 'dateMAJ',
                render: (sousClient: SousClientApi) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {sousClient.dateMAJ
                            ? new Date(sousClient.dateMAJ).toLocaleDateString()
                            : null}
                    </Typography>
                ),
            },
            {
                isDisplayed: true,
                label: 'Dos...',
                headerTooltip: 'Dossiers',
                field: 'nbDossier',
                sortable: true,
                render: (sousClient: SousClientApi) => (
                    <Tooltip title="Accéder aux dossiers">
                        <Box
                            sx={{
                                color: 'text.gris70',
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '5px',
                                cursor: 'pointer',
                            }}
                        >
                            <FaOptionIcon icon={['fasl', 'folder']} />
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris70"
                                component="span"
                            >
                                {sousClient.nbDossier}
                            </Typography>
                        </Box>
                    </Tooltip>
                ),
                onClickCell: handleRedirectToDossiers,
            },
            {
                label: 'Actions',
                displayLabel: false,
                render: (sousClient: SousClientApi) => (
                    <ActionMenuSocietes sousClient={sousClient} />
                ),
                isDisplayed: true,
                sortable: false,
                sx: {
                    display: {
                        xs: 'none',
                        sm: 'none',
                        md: 'table-cell',
                        lg: 'table-cell',
                        xl: 'table-cell',
                    },
                },
            },
        ];

        if (showPrescripteur) {
            baseColumns.unshift({
                isDisplayed: true,
                label: 'Prescripteur',
                sortable: true,
                onClickCell: handlePrescripteurClick,
                render: (sousClient: SousClientApi) => {
                    if (!sousClient.idPrescripteur || !sousClient.raisonSocialePrescripteur) {
                        return null;
                    }

                    return (
                        <Tooltip title={sousClient.raisonSocialePrescripteur}>
                            <Box
                                sx={{
                                    borderRadius: '16px',
                                    width: '32px',
                                    height: '32px',
                                    margin: '0px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CustomAvatar
                                    sx={{ width: '32px', height: '32px' }}
                                    firstName={sousClient.raisonSocialePrescripteur}
                                />
                            </Box>
                        </Tooltip>
                    );
                },
            });
        }

        return baseColumns;
    }, [handlePrescripteurClick, handleRedirectToDossiers, palette.grey, showPrescripteur]);

    const filters: FiltersDatatableList = React.useMemo(() => {
        const allFilters: FiltersDatatableList = [
            {
                type: 'selectApi',
                label: 'Sociétés',
                field: 'orSousClientId',
                icon: 'building-user',
                showResultOnSearchOnly: true,
                searchFunction: searchSociete,
                renderItem: (item: Record<string, unknown>) => item.raisonSociale as string,
                valueMap: (item: Record<string, unknown>) => [item.id as unknown as string],
            },
            {
                label: 'Mes groupes',
                field: 'objectgroups',
                type: 'selectApi',
                icon: 'buildings',
                searchFunction: searchTiersObjectGroup,
                renderItem: (item: TiersObjectGroup) => item.label as string,
                renderIcon: (item: TiersObjectGroup, defaultIcon) => {
                    const icon = item?.icon || defaultIcon;
                    const color = item?.color;

                    return (
                        <FaOptionIcon
                            sx={{
                                fontSize: '12px',
                                lineHeight: '0',
                                color: color ? `${color}.main` : 'inherit',
                            }}
                            icon={['fasl', icon as IconName]}
                        />
                    );
                },
                valueMap: (item: TiersObjectGroup) => item.id as unknown as string,
            },
        ];

        return allFilters;
    }, [searchSociete, searchTiersObjectGroup]);

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Ajouter une société',
                icon: <FaOptionIcon {...plusIcon.props} />,
                action: handleCreateClient,
            },
        ],
        [handleCreateClient],
    );

    useDumbActionsContext(contextActions);

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des sociétés."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    // reFetch sous-client api datas on slice filter change
    // the companies list can be load from user preferences -> groups
    React.useEffect(() => {
        dispatch(getSousClients({}) as unknown as AnyAction);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasource.filters, dispatch]);

    useDumbActionsContext(contextActions);

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={8}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Ajouter une société"
                        icon={plusIcon}
                        onClick={handleCreateClient}
                        actionType="modal"
                    />
                }
            />

            {datasource && (
                <StoreDatatable
                    dataSource={datasource}
                    columns={columns}
                    fetchData={getSousClients}
                    filters={filters}
                    localStorageKey="FlexyStoreDatatableSociete"
                    observers={[xIdSociete]}
                    sx={{ marginBottom: '2.25em', cursor: 'pointer' }}
                    onClickRow={handleRowClick}
                    computeSxRowHover={() => ({
                        '.raison-sociale': {
                            transition: 'color 0.25s ease',
                            color: 'primary.main',
                        },
                    })}
                />
            )}
        </SkeletonLoader>
    );
};

export default ListeSousClient;
